// src/pages/guides/AttackSurfaceChecklistGuide.js
// Attack Surface Monitoring Checklist for SMB IT Teams - SEO Guide (G5)

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function AttackSurfaceChecklistGuide() {
  const startTrial = useTrialCta('guide_trial:attack-surface-checklist');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Attack Surface Monitoring Checklist for SMB IT Teams",
    "description": "A practical weekly, monthly, and quarterly attack surface monitoring checklist built for IT teams of 1-5 people. Printable and actionable.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2026-03-06T00:00:00-05:00",
    "dateModified": "2026-08-25T00:00:00-04:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/attack-surface-checklist",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/attack-surface-checklist",
    "keywords": ["attack surface monitoring checklist", "attack surface management SMB", "security monitoring checklist", "IT security checklist small business"]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "Attack Surface Checklist" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="Attack Surface Monitoring Checklist for SMBs | DriftAlarm"
        description="A practical weekly, monthly, and quarterly attack surface monitoring checklist built for IT teams of 1-5 people. Printable and actionable."
        path="/guides/attack-surface-checklist"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/attack-surface-checklist')].filter(Boolean)}
      />

      {/* Navigation Bar */}
      <PublicHeader activeLink="guides" />

      {/* Main Content */}
      <main className="guide-content">
        {/* Hero Section */}
        <section className="guide-hero">
          <h1>Attack Surface Monitoring Checklist for SMB IT Teams</h1>
          <GuideByline path="/guides/attack-surface-checklist" />
          <GuideAnswer path="/guides/attack-surface-checklist" />
          <p className="guide-hero-intro">
            You don't need a 20-person security team to monitor your attack surface. You need a
            checklist, a cadence, and the discipline to follow it. This guide gives you all three —
            built for IT teams of one to five people who are already stretched thin.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>A 15-minute weekly attack surface check (7 items)</li>
              <li>A 30-minute monthly deep review (4 items)</li>
              <li>A quarterly strategic review (3 items)</li>
              <li>Which open-source tools cover each step</li>
              <li>How to automate 90% of this checklist</li>
              <li>Three actions you can take in the next 10 minutes</li>
            </ul>
          </div>

          <p style={{
            fontFamily: "var(--font-display, 'OCR-A', 'Courier New', monospace)",
            fontSize: '0.8rem',
            color: 'var(--text-muted, #808080)',
            letterSpacing: '1px',
            marginBottom: '20px'
          }}>
            12 min read
          </p>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>&rarr;</span>
            </button>
            <Link to="/guides/attack-surface-monitoring" className="da-btn da-btn--secondary">
              Read the full Attack Surface Monitoring Guide
            </Link>
          </div>
        </section>

        {/* Table of Contents */}
        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#who-this-is-for">Who This Checklist Is For</a></li>
            <li><a href="#weekly-check">Weekly: 15-Minute Check</a></li>
            <li><a href="#monthly-review">Monthly: 30-Minute Deep Review</a></li>
            <li><a href="#quarterly-review">Quarterly: Strategic Review</a></li>
            <li><a href="#tools-you-need">Tools You Need</a></li>
            <li><a href="#getting-started">Getting Started in 10 Minutes</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
          </ul>
        </nav>

        {/* Section: Who This Is For */}
        <section id="who-this-is-for" className="guide-section">
          <h2>Who This Checklist Is For</h2>
          <p>
            This checklist is designed for the IT generalist who wears too many hats. You manage the
            network, handle help desk tickets, run backups, keep the website up, and somewhere in there
            you're also supposed to be doing "security." Sound familiar?
          </p>
          <p>
            If your organization has between 10 and 500 employees, no dedicated security hire, and
            at least a few internet-facing assets (a website, a mail server, maybe a VPN), this
            checklist is for you. It's tool-agnostic — you can do most of this with free tools and
            a browser. But we'll also show you how to automate the boring parts so you can focus
            on what actually needs your attention.
          </p>

          <div className="callout callout-info">
            <div className="callout-title">The Goal Is Consistency, Not Perfection</div>
            <div className="callout-content">
              <p>
                A 15-minute weekly check you actually do is infinitely more valuable than a
                4-hour monthly audit you keep pushing to next week. This checklist is designed
                to be short enough that you'll actually complete it every time.
              </p>
            </div>
          </div>

          <p>
            What makes attack surface monitoring different from vulnerability scanning? Vulnerability
            scanners check known assets for known CVEs. Attack surface monitoring answers a more
            fundamental question: <strong>what assets do you actually have exposed to the internet
            right now?</strong> New subdomains, new open ports, DNS changes, expired certificates —
            these are the gaps that attackers exploit before your vulnerability scanner ever sees them.
          </p>
        </section>

        {/* Section: Weekly Check */}
        <section id="weekly-check" className="guide-section">
          <h2>The 15-Minute Weekly Attack Surface Check</h2>
          <p>
            Block 15 minutes every Monday morning. Put it on your calendar. Treat it like a
            standup meeting — quick, focused, non-negotiable. Here are the seven items to check
            every single week.
          </p>

          <div className="quickstart-list">
            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Item 1</span>
                <span className="quickstart-day-title">Review New Subdomains Discovered</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Check for any new subdomains that appeared since last week</li>
                <li>Verify each new subdomain is authorized and expected</li>
                <li>Flag unknown subdomains for immediate investigation</li>
                <li>Look for patterns: staging servers, test environments, shadow IT</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Item 2</span>
                <span className="quickstart-day-title">Check for New Open Ports</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Compare this week's port scan results against last week's baseline</li>
                <li>Investigate any new ports that weren't open before</li>
                <li>Pay special attention to management ports (22, 3389, 8080, 8443)</li>
                <li>Confirm any new services have proper authorization</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Item 3</span>
                <span className="quickstart-day-title">Verify SSL Certificate Status</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Check certificates expiring within 30 days</li>
                <li>Verify no certificates have already expired</li>
                <li>Confirm certificate chains are valid and trusted</li>
                <li>Initiate renewal for anything expiring within 14 days</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Item 4</span>
                <span className="quickstart-day-title">Review DNS Record Changes</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Check for any DNS records added, modified, or deleted</li>
                <li>Look for dangling CNAME records pointing to decommissioned services</li>
                <li>Verify MX records haven't been tampered with</li>
                <li>Confirm SPF/DKIM/DMARC records are intact</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Item 5</span>
                <span className="quickstart-day-title">Check for Exposed Admin Panels</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Scan for common admin paths (/admin, /wp-admin, /login, /phpmyadmin)</li>
                <li>Verify all admin interfaces require MFA</li>
                <li>Confirm admin panels are not accessible from the public internet</li>
                <li>Check for any new management interfaces that appeared</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Item 6</span>
                <span className="quickstart-day-title">Review Vulnerability Scan Results</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Check for any new critical or high-severity vulnerabilities</li>
                <li>Verify that previously identified vulns are being remediated</li>
                <li>Look for newly published CVEs affecting your tech stack</li>
                <li>Prioritize anything internet-facing with a public exploit</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Item 7</span>
                <span className="quickstart-day-title">Check Drift Alerts</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Review any configuration drift alerts from the past week</li>
                <li>Investigate unexpected changes to running services</li>
                <li>Check for technology version changes (upgrades or downgrades)</li>
                <li>Verify that all changes correspond to approved change requests</li>
              </ul>
            </div>
          </div>

          <p>
            That's the entire weekly check. Seven items, fifteen minutes. If you find something
            concerning during the check, don't try to fix it on the spot — log it, assign it a
            priority, and schedule the remediation. The weekly check is for <strong>detection</strong>,
            not resolution.
          </p>
        </section>

        {/* Mid-article CTA */}
        <section className="guide-section">
          <div className="callout callout-success">
            <div className="callout-title">Automate This Entire Checklist</div>
            <div className="callout-content">
              <p>
                DriftAlarm runs all seven weekly checks automatically — subdomain discovery, port
                monitoring, SSL tracking, DNS change detection, admin panel scanning, vulnerability
                assessment, and drift alerting. Instead of spending 15 minutes checking manually,
                you spend 2 minutes reviewing what DriftAlarm already found.
              </p>
              <p>
                <Link to="/upgrade" style={{ color: 'var(--neon-green, #39FF14)' }}>
                  Start your 30-day trial &rarr;
                </Link>
              </p>
            </div>
          </div>
        </section>

        {/* Section: Monthly Review */}
        <section id="monthly-review" className="guide-section">
          <h2>Monthly Deep Review (30 Minutes)</h2>
          <p>
            Once a month, block 30 minutes for a deeper look. This is where you catch the
            slow-moving risks that don't trigger weekly alerts — the gradual drift, the forgotten
            assets, the trends that only become visible over time.
          </p>

          <div className="quickstart-list">
            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Monthly 1</span>
                <span className="quickstart-day-title">Full Asset Inventory Audit</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Compare your asset register against discovery scan results</li>
                <li>Identify any assets in scans that aren't in your register (shadow IT)</li>
                <li>Remove decommissioned assets that no longer resolve</li>
                <li>Confirm asset ownership is current — people leave, roles change</li>
                <li>Update criticality ratings if business context has changed</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Monthly 2</span>
                <span className="quickstart-day-title">Technology Stack Review</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Review all detected technologies across your assets</li>
                <li>Check for end-of-life (EOL) software approaching or past end of support</li>
                <li>Look for technology version inconsistencies across environments</li>
                <li>Verify all web frameworks and CMS platforms are on supported versions</li>
                <li>Flag any technology that requires an upgrade plan</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Monthly 3</span>
                <span className="quickstart-day-title">WHOIS and Registration Verification</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Check domain expiration dates — renew anything within 90 days</li>
                <li>Verify registrar lock is enabled on critical domains</li>
                <li>Confirm WHOIS contact info is accurate and monitored</li>
                <li>Check for unauthorized domain transfers or NS record changes</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Monthly 4</span>
                <span className="quickstart-day-title">Risk Score Trend Analysis</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Review your overall risk score trend over the past 30 days</li>
                <li>Identify which assets are driving risk score increases</li>
                <li>Check if remediation efforts are actually reducing your score</li>
                <li>Compare against your target risk posture</li>
                <li>Adjust priorities if certain assets are chronically risky</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section: Quarterly Review */}
        <section id="quarterly-review" className="guide-section">
          <h2>Quarterly Strategic Review</h2>
          <p>
            Every quarter, step back and look at the bigger picture. This isn't about individual
            vulnerabilities — it's about whether your monitoring program is keeping pace with
            how your organization is growing and changing.
          </p>

          <div className="quickstart-list">
            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Quarterly 1</span>
                <span className="quickstart-day-title">Scope Expansion</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Has the company acquired new domains, IPs, or cloud accounts?</li>
                <li>Are there new SaaS tools that expose branded login pages?</li>
                <li>Has the company merged with or acquired another organization?</li>
                <li>Add any new assets to your monitoring scope</li>
                <li>Retire monitoring for assets that have been fully decommissioned</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Quarterly 2</span>
                <span className="quickstart-day-title">Vendor and Third-Party Surface Review</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Review vendor integrations that connect to your infrastructure</li>
                <li>Check for third-party services hosting content on your domains</li>
                <li>Verify that vendor-managed assets meet your security standards</li>
                <li>Assess whether any vendor relationships have changed or ended</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Quarterly 3</span>
                <span className="quickstart-day-title">Compliance Evidence Collection</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Export scan reports and remediation evidence for audit readiness</li>
                <li>Document your monitoring cadence and coverage for compliance</li>
                <li>Verify that monitoring meets any regulatory requirements (SOC 2, ISO 27001, HIPAA)</li>
                <li>Archive quarterly summary for board or leadership reporting</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section: Tools */}
        <section id="tools-you-need" className="guide-section">
          <h2>Tools You Need</h2>
          <p>
            You can build a monitoring program with open-source tools. It takes more manual effort,
            but it works. Here's what covers each checklist item — and how DriftAlarm automates
            each step so you don't have to stitch it together yourself.
          </p>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Checklist Item</th>
                  <th>Open-Source Tools</th>
                  <th>DriftAlarm Automation</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Subdomain discovery</strong></td>
                  <td>Amass, Subfinder, crt.sh</td>
                  <td>Automated weekly discovery scans with change alerts</td>
                </tr>
                <tr>
                  <td><strong>Port monitoring</strong></td>
                  <td>Nmap, Masscan, RustScan</td>
                  <td>Daily port scans with baseline comparison and drift detection</td>
                </tr>
                <tr>
                  <td><strong>SSL certificate tracking</strong></td>
                  <td>testssl.sh, sslyze, certbot</td>
                  <td>Continuous certificate monitoring with expiry alerts</td>
                </tr>
                <tr>
                  <td><strong>DNS change detection</strong></td>
                  <td>dig + cron scripts, DNSdiff</td>
                  <td>Automated DNS baseline with change notifications</td>
                </tr>
                <tr>
                  <td><strong>Admin panel scanning</strong></td>
                  <td>Nuclei, httpx, ffuf</td>
                  <td>Vulnerability scans include admin path detection</td>
                </tr>
                <tr>
                  <td><strong>Vulnerability scanning</strong></td>
                  <td>Nuclei, OpenVAS, Nikto</td>
                  <td>Daily vulnerability scans with AI-assisted prioritization</td>
                </tr>
                <tr>
                  <td><strong>Drift alerting</strong></td>
                  <td>Custom scripts + diff</td>
                  <td>32 built-in drift rules with notification channels</td>
                </tr>
                <tr>
                  <td><strong>Asset inventory</strong></td>
                  <td>Spreadsheet + manual updates</td>
                  <td>Automatic asset register with technology fingerprinting</td>
                </tr>
                <tr>
                  <td><strong>Tech stack review</strong></td>
                  <td>WhatWeb, Wappalyzer, httpx</td>
                  <td>Technology normalization with EOL tracking</td>
                </tr>
                <tr>
                  <td><strong>Risk scoring</strong></td>
                  <td>Manual spreadsheet formulas</td>
                  <td>Automated risk scores with trend tracking per asset</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="callout callout-warning">
            <div className="callout-title">The DIY Tax</div>
            <div className="callout-content">
              <p>
                Stitching open-source tools together works, but it takes time — typically 2-4 hours
                per week to run, aggregate, compare, and investigate results manually. That's time
                most SMB IT teams don't have. The real question is whether your time is better
                spent running Nmap or investigating the one finding that actually matters.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Getting Started */}
        <section id="getting-started" className="guide-section">
          <h2>Getting Started Today: 3 Actions in 10 Minutes</h2>
          <p>
            Don't wait until next Monday. Here are three things you can do in the next 10 minutes
            to start monitoring your attack surface.
          </p>

          <div className="step-cards">
            <div className="step-card">
              <div className="step-card-number">1</div>
              <div className="step-card-title">List Your Assets</div>
              <p style={{
                fontFamily: "var(--font-body, 'Share Tech Mono', 'Consolas', monospace)",
                fontSize: '0.85rem',
                color: 'var(--text-secondary, #b0b0b0)',
                lineHeight: '1.6',
                marginTop: '10px'
              }}>
                Open a spreadsheet. Write down every domain, IP address, and cloud service your
                company exposes to the internet. Don't overthink it — you can refine later. Just
                get the list started.
              </p>
            </div>
            <div className="step-card">
              <div className="step-card-number">2</div>
              <div className="step-card-title">Schedule Your First Weekly Check</div>
              <p style={{
                fontFamily: "var(--font-body, 'Share Tech Mono', 'Consolas', monospace)",
                fontSize: '0.85rem',
                color: 'var(--text-secondary, #b0b0b0)',
                lineHeight: '1.6',
                marginTop: '10px'
              }}>
                Put a 15-minute recurring calendar event on Monday mornings. Label it "Attack
                Surface Check." Invite yourself. Protect this time — it's the most important
                15 minutes of your security week.
              </p>
            </div>
            <div className="step-card">
              <div className="step-card-number">3</div>
              <div className="step-card-title">Run Your First Scan</div>
              <p style={{
                fontFamily: "var(--font-body, 'Share Tech Mono', 'Consolas', monospace)",
                fontSize: '0.85rem',
                color: 'var(--text-secondary, #b0b0b0)',
                lineHeight: '1.6',
                marginTop: '10px'
              }}>
                Pick one domain from your list. Run a discovery scan with DriftAlarm (takes just
                minutes) or use Amass and Nmap manually. Either way, you'll immediately see
                what's exposed — and that's the first step.
              </p>
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="guide-section">
          <h2>Related Security Guides</h2>
          <p>
            This checklist gets you started. These guides go deeper on specific topics:
          </p>

          <div className="playbook-links">
            <Link to="/guides/attack-surface-monitoring" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Attack Surface Monitoring Guide</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/guides/configuration-drift" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Configuration Drift Detection Guide</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/guides/easm-vs-vulnerability-scanning" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">EASM vs Vulnerability Scanning</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/guides/easm-compliance" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">EASM for Compliance</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/attack-surface-monitoring" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Attack Surface Monitoring Overview</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="guide-section">
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <h3 style={{
              fontFamily: "var(--font-display, 'OCR-A', 'Courier New', monospace)",
              fontSize: '1.4rem',
              color: 'var(--text-primary, #ffffff)',
              marginBottom: '15px',
              letterSpacing: '1px'
            }}>
              Start Your 30-Day Trial
            </h3>
            <p style={{
              fontFamily: "var(--font-body, 'Share Tech Mono', 'Consolas', monospace)",
              fontSize: '1rem',
              color: 'var(--text-secondary, #b0b0b0)',
              marginBottom: '25px',
              lineHeight: '1.7'
            }}>
              First scan in minutes. No credit card required. See what attackers see
              before they see it first.
            </p>
            <div className="guide-cta-group">
              <Link to="/upgrade" className="da-btn da-btn--primary">
                <span>Start Your 30-Day Trial</span>
                <span>&rarr;</span>
              </Link>
            </div>
          </div>
        </section>
        <GuideFAQ path="/guides/attack-surface-checklist" />
      </main>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}

export default AttackSurfaceChecklistGuide;
